/**
 * LeadManagement
 * Manage incoming leads with filtering, status updates, expand details, CSV export, and pagination.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

const STATUSES = ['all', 'submitted', 'contacted', 'converted', 'closed'];
const PAGE_SIZE = 20;

const PROVINCES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland', NS: 'Nova Scotia', ON: 'Ontario', PE: 'PEI',
  QC: 'Quebec', SK: 'Saskatchewan', NT: 'NWT', NU: 'Nunavut', YT: 'Yukon',
};

// ── Mock data ───────────────────────────────────────────────────────────────

function generateMockLeads() {
  const names = [
    'Sarah Johnson', 'Marc Dupont', 'Emily Chen', 'James Wilson', 'Priya Patel',
    'Robert Fraser', 'Marie Leblanc', 'David Kim', 'Jessica Brown', 'Ahmed Hassan',
    'Karen Taylor', 'Michel Tremblay', 'Lisa Wang', 'Brian O\'Connor', 'Sophia Martin',
    'Chris Evans', 'Megan Lee', 'Thomas Young', 'Nancy White', 'Omar Ali',
    'Jennifer Adams', 'Patrick Gagnon', 'Samantha Green', 'Derek Hall', 'Aisha Khan',
  ];
  const sectors = ['healthcare', 'telecom', 'insurance', 'finance', 'utilities', 'home-services'];
  const statuses = ['submitted', 'contacted', 'converted', 'closed'];
  const budgets = ['under-50', '50-100', '100-250', '250-500', '500-1000'];
  const provinces = ['ON', 'QC', 'BC', 'AB', 'MB', 'SK', 'NS'];
  const sources = ['web', 'referral', 'affiliate', 'direct', 'social'];

  return names.map((name, i) => ({
    id: `lead-${i + 1}`,
    name,
    email: `${name.toLowerCase().replace(/[' ]/g, '.')}@example.com`,
    phone: `(${416 + (i % 10)}) ${500 + i}-${1000 + i * 3}`,
    sector: sectors[i % sectors.length],
    budget: budgets[i % budgets.length],
    status: statuses[i % statuses.length],
    province: provinces[i % provinces.length],
    source: sources[i % sources.length],
    message: i % 3 === 0 ? `I'm looking for a reliable ${sectors[i % sectors.length]} provider in my area. Please send details about your plans.` : '',
    date: new Date(Date.now() - i * 86400000 * 1.5).toISOString(),
  }));
}

function exportLeadsCSV(leads) {
  const headers = ['Name', 'Email', 'Phone', 'Sector', 'Budget', 'Status', 'Province', 'Source', 'Date'];
  const rows = leads.map((l) => [
    l.name, l.email, l.phone, l.sector, l.budget, l.status,
    PROVINCES[l.province] || l.province, l.source,
    new Date(l.date).toLocaleDateString('en-CA'),
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${v}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function LeadManagement() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch leads
  const { data: leadsRaw } = useQuery({
    queryKey: ['provider-leads'],
    queryFn: () => api.get('/api/provider-portal/leads').catch(() => null),
    placeholderData: null,
  });

  const allLeads = leadsRaw?.leads ?? generateMockLeads();

  // Status update mutation
  const updateStatus = useMutation({
    mutationFn: ({ leadId, status }) =>
      api.patch(`/api/provider-portal/leads/${leadId}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-leads'] }),
  });

  // Filtering
  const filteredLeads = useMemo(() => {
    if (statusFilter === 'all') return allLeads;
    return allLeads.filter((l) => l.status === statusFilter);
  }, [allLeads, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredLeads.length / PAGE_SIZE);
  const pagedLeads = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredLeads.slice(start, start + PAGE_SIZE);
  }, [filteredLeads, page]);

  // Stats
  const stats = useMemo(() => {
    const total = allLeads.length;
    const contacted = allLeads.filter((l) => l.status === 'contacted' || l.status === 'converted').length;
    const converted = allLeads.filter((l) => l.status === 'converted').length;
    return {
      total,
      contactedPct: total > 0 ? ((contacted / total) * 100).toFixed(1) : '0.0',
      conversionRate: total > 0 ? ((converted / total) * 100).toFixed(1) : '0.0',
    };
  }, [allLeads]);

  const handleStatusChange = useCallback((leadId, newStatus) => {
    updateStatus.mutate({ leadId, status: newStatus });
  }, [updateStatus]);

  const toggleRow = useCallback((id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
          Lead Management
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          View and manage incoming quote requests from potential customers.
        </p>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Leads', value: stats.total },
          { label: 'Contacted', value: `${stats.contactedPct}%` },
          { label: 'Conversion Rate', value: `${stats.conversionRate}%` },
        ].map((s) => (
          <div key={s.label} style={{
            flex: '1 1 150px', padding: '14px 18px', backgroundColor: '#fff',
            borderRadius: '10px', border: '1px solid #e2e8f0', textAlign: 'center',
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters + export */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { setStatusFilter(s); setPage(1); }}
            style={{
              padding: '6px 14px', fontSize: '13px', fontWeight: '600', borderRadius: '8px',
              border: statusFilter === s ? 'none' : '1px solid #d1d5db',
              backgroundColor: statusFilter === s ? '#3b82f6' : '#fff',
              color: statusFilter === s ? '#fff' : '#475569',
              cursor: 'pointer', textTransform: 'capitalize',
            }}
          >
            {s}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => exportLeadsCSV(filteredLeads)}
          style={{
            padding: '6px 16px', fontSize: '13px', fontWeight: '600', borderRadius: '8px',
            border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Leads table */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              {['Name', 'Email', 'Phone', 'Sector', 'Budget', 'Status', 'Date', 'Actions'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: '#64748b', fontWeight: '600', borderBottom: '2px solid #e2e8f0', fontSize: '12px' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedLeads.map((lead) => (
              <React.Fragment key={lead.id}>
                <tr
                  onClick={() => toggleRow(lead.id)}
                  style={{
                    borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                    backgroundColor: expandedRow === lead.id ? '#f0f9ff' : 'transparent',
                  }}
                >
                  <td style={{ padding: '10px 12px', fontWeight: '500', color: '#1e293b' }}>{lead.name}</td>
                  <td style={{ padding: '10px 12px', color: '#475569' }}>{lead.email}</td>
                  <td style={{ padding: '10px 12px', color: '#475569' }}>{lead.phone}</td>
                  <td style={{ padding: '10px 12px', color: '#475569', textTransform: 'capitalize' }}>{lead.sector}</td>
                  <td style={{ padding: '10px 12px', color: '#475569' }}>{lead.budget || '--'}</td>
                  <td style={{ padding: '10px 12px' }}><StatusBadge status={lead.status} /></td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>{new Date(lead.date).toLocaleDateString('en-CA')}</td>
                  <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {lead.status === 'submitted' && (
                        <button
                          type="button"
                          onClick={() => handleStatusChange(lead.id, 'contacted')}
                          style={{ padding: '4px 10px', fontSize: '11px', fontWeight: '600', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer' }}
                        >
                          Mark Contacted
                        </button>
                      )}
                      {lead.status === 'contacted' && (
                        <button
                          type="button"
                          onClick={() => handleStatusChange(lead.id, 'converted')}
                          style={{ padding: '4px 10px', fontSize: '11px', fontWeight: '600', borderRadius: '6px', border: 'none', backgroundColor: '#16a34a', color: '#fff', cursor: 'pointer' }}
                        >
                          Mark Converted
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Expanded detail row */}
                {expandedRow === lead.id && (
                  <tr>
                    <td colSpan={8} style={{ padding: '12px 24px 16px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', fontSize: '13px' }}>
                        <div>
                          <div style={{ color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>Province</div>
                          <div style={{ color: '#1e293b' }}>{PROVINCES[lead.province] || lead.province || '--'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>Source</div>
                          <div style={{ color: '#1e293b', textTransform: 'capitalize' }}>{lead.source || '--'}</div>
                        </div>
                        <div style={{ flex: '1 1 300px' }}>
                          <div style={{ color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>Message</div>
                          <div style={{ color: '#1e293b', lineHeight: '1.5' }}>{lead.message || 'No message provided.'}</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {pagedLeads.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                  No leads found for the selected filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '6px 12px', fontSize: '13px', borderRadius: '6px',
              border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#475569',
              cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1,
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '6px 12px', fontSize: '13px', borderRadius: '6px',
              border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#475569',
              cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.5 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
